import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail, ChevronDown } from "lucide-react";
import { BRAND, SERVICES } from "@/lib/data";

function useScrolled() {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return scrolled;
}

export default function Header() {
    const scrolled = useScrolled();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setMobileOpen(false);
        setServicesOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 bg-bone transition-shadow duration-300 ${
                scrolled ? "shadow-[0_1px_0_0_rgba(26,26,26,0.08)]" : ""
            }`}
        >
            <TopBar />

            {/* Main bar — logo LEFT · nav RIGHT */}
            <div className="container-wide flex items-center justify-between h-24 md:h-32">
                {/* Mobile hamburger */}
                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-label="Toggle menu"
                    className="lg:hidden p-2 text-ink -ml-2"
                >
                    {mobileOpen ? <X size={24} /> : <Menu size={24} />}
                </button>

                {/* Logo — left aligned */}
                <Link
                    to="/"
                    data-testid="header-logo-link"
                    className="flex items-center lg:flex-shrink-0 shrink-0"
                    aria-label="Brazier Building & Design — Home"
                >
                    <img
                        src={BRAND.logoUrlFooter}
                        alt="Brazier Building & Design"
                        className="h-14 md:h-20 w-auto object-contain shrink-0"
                        style={{ maxWidth: "none" }}
                    />
                </Link>

                <DesktopNav servicesOpen={servicesOpen} setServicesOpen={setServicesOpen} />

                {/* Mobile email (right) */}
                <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="header-email-link-mobile"
                    aria-label={`Email ${BRAND.email}`}
                    className="lg:hidden p-2 text-clay -mr-2"
                >
                    <Mail size={22} strokeWidth={1.6} />
                </a>
            </div>

            {mobileOpen && <MobileMenu />}
        </header>
    );
}

function TopBar() {
    return (
        <div className="hidden md:block border-b border-line bg-bone">
            <div className="container-wide h-10 flex items-center justify-center">
                <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="top-bar-email"
                    className="inline-flex items-center gap-2 text-[12px] text-ink2 hover:text-clay transition-colors"
                >
                    <Mail size={13} strokeWidth={1.6} className="text-clay" />
                    <span>{BRAND.email}</span>
                </a>
            </div>
        </div>
    );
}

const navLinkClass = ({ isActive }) =>
    `text-[13px] tracking-[0.02em] font-medium transition-colors text-ink hover:text-clay ${
        isActive ? "text-clay" : ""
    }`;

function DesktopNav({ servicesOpen, setServicesOpen }) {
    return (
        <nav className="hidden lg:flex items-center gap-10">
            {/* Services dropdown */}
            <div
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
            >
                <Link
                    to="/#services"
                    data-testid="nav-services"
                    className="inline-flex items-center gap-1.5 text-[13px] tracking-[0.02em] font-medium text-ink hover:text-clay transition-colors"
                >
                    Services
                    <ChevronDown size={14} strokeWidth={1.6} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
                </Link>
                {servicesOpen && (
                    <div
                        data-testid="services-dropdown"
                        className="absolute right-0 top-full pt-6 w-72"
                    >
                        <div className="bg-bone border border-line shadow-xl">
                            {SERVICES.map((s) => (
                                <Link
                                    key={s.slug}
                                    to={`/#${s.slug}`}
                                    data-testid={`nav-service-${s.slug}`}
                                    className="block px-6 py-4 text-sm text-ink hover:bg-bone2 hover:text-clay border-b border-line last:border-b-0 transition-colors"
                                >
                                    {s.title}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <NavLink to="/projects" data-testid="nav-projects" className={navLinkClass}>
                Projects
            </NavLink>

            <NavLink to="/who-we-are" data-testid="nav-who-we-are" className={navLinkClass}>
                Who We Are
            </NavLink>

            <NavLink to="/blog" data-testid="nav-blog" className={navLinkClass}>
                Blog
            </NavLink>

            <NavLink to="/contact" data-testid="nav-contact-us" className={navLinkClass}>
                Contact Us
            </NavLink>
        </nav>
    );
}

function MobileMenu() {
    return (
        <div
            data-testid="mobile-menu"
            className="lg:hidden bg-bone border-t border-line"
        >
            <div className="container-wide py-6 flex flex-col gap-1">
                <Link to="/#services" data-testid="mobile-nav-services" className="py-3 text-base tracking-[0.04em] font-medium text-ink border-b border-line">Services</Link>
                <Link to="/projects" data-testid="mobile-nav-projects" className="py-3 text-base tracking-[0.04em] font-medium text-ink border-b border-line">Projects</Link>
                <Link to="/who-we-are" data-testid="mobile-nav-who-we-are" className="py-3 text-base tracking-[0.04em] font-medium text-ink border-b border-line">Who We Are</Link>
                <Link to="/blog" data-testid="mobile-nav-blog" className="py-3 text-base tracking-[0.04em] font-medium text-ink border-b border-line">Blog</Link>
                <Link to="/contact" data-testid="mobile-nav-contact-us" className="py-3 text-base tracking-[0.04em] font-medium text-ink border-b border-line">Contact Us</Link>

                <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="mobile-email-link"
                    className="mt-5 inline-flex items-center gap-2 text-clay font-medium"
                >
                    <Mail size={16} strokeWidth={1.6} />
                    {BRAND.email}
                </a>
            </div>
        </div>
    );
}
